<script setup>
import { ref, watch, onMounted, computed } from 'vue'

import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import Checkbox from 'primevue/checkbox'
import { useAppToast } from '@/shared/lib/useAppToast'

import { getAllBrands } from '@/modules/admin/brands/api/brands.service'
import { getAllCategories } from '@/modules/admin/categories/api/categories.service'
import { getAllAttributes } from '@/modules/admin/products/attributes/api/attributes.service'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  mode: {
    type: String,
    default: 'create'
  },
  productType: {
    type: Object,
    default: null
  },
  submitting: {
    type: Boolean,
    default: false
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'submit'])

const toast = useAppToast()

const name = ref('')
const collectionName = ref('')
const modelCode = ref('')
const description = ref('')
const descriptionMaxLength = 2000
const categoryId = ref(null)
const brandId = ref(null)
const attributeIds = ref([])
const attributeDefinitions = ref([])
const scopes = [{ label: 'Общая', value: 1 }, { label: 'Вариативная', value: 0 }]
const isActive = ref(true)

const brands = ref([])
const categories = ref([])
const attributes = ref([])
const loadingOptions = ref(false)

const localErrors = ref({
  name: '',
  description: '',
  categoryId: '',
  brandId: '',
  attributeIds: ''
})

const dialogTitle = computed(() =>
  props.mode === 'edit' ? 'Редактирование типа товара' : 'Создание типа товара'
)

const submitLabel = computed(() =>
  props.mode === 'edit' ? 'Сохранить' : 'Создать'
)

async function loadData() {
  loadingOptions.value = true

  const [brandsResult, categoriesResult, attributesResult] = await Promise.allSettled([
    getAllBrands(),
    getAllCategories(),
    getAllAttributes()
  ])

  brands.value =
    brandsResult.status === 'fulfilled' && Array.isArray(brandsResult.value)
      ? brandsResult.value
      : []

  categories.value =
    categoriesResult.status === 'fulfilled' && Array.isArray(categoriesResult.value)
      ? categoriesResult.value
      : []

  attributes.value =
    attributesResult.status === 'fulfilled' && Array.isArray(attributesResult.value)
      ? attributesResult.value
      : []

  const criticalError =
    brandsResult.status === 'rejected'
      ? brandsResult.reason
      : categoriesResult.status === 'rejected'
        ? categoriesResult.reason
        : null

  if (criticalError) {
    toast.error(criticalError.message || 'Ошибка загрузки данных')
  } else if (attributesResult.status === 'rejected') {
    toast.warn('Атрибуты не загружены. Тип продукта можно создать и без них.')
  }

  loadingOptions.value = false
}

function clearLocalErrors() {
  localErrors.value = {
    name: '',
    description: '',
    categoryId: '',
    brandId: '',
    attributeIds: ''
  }
}

function resetForm() {
  name.value = ''
  collectionName.value = ''
  modelCode.value = ''
  description.value = ''
  categoryId.value = null
  brandId.value = null
  attributeIds.value = []
  attributeDefinitions.value = []
  isActive.value = true
  clearLocalErrors()
}

function getFieldError(field) {
  return (
    localErrors.value[field] ||
    props.fieldErrors?.[field] ||
    props.fieldErrors?.[field.charAt(0).toUpperCase() + field.slice(1)] ||
    ''
  )
}

function hasFieldError(field) {
  return Boolean(getFieldError(field))
}

function validateForm() {
  clearLocalErrors()

  if (!name.value.trim()) {
    localErrors.value.name = 'Введите название'
  }

  if (description.value.length > descriptionMaxLength) {
    localErrors.value.description = 'Описание не должно превышать 2000 символов'
  }

  if (!categoryId.value) {
    localErrors.value.categoryId = 'Выберите категорию'
  }

  if (!brandId.value) {
    localErrors.value.brandId = 'Выберите бренд'
  }

  if (attributeDefinitions.value.some(a => a.scope === 1 && !a.commonCharacteristicValueId)) {
    localErrors.value.attributeIds = 'Выберите значение'
  }

  return !Object.values(localErrors.value).some(Boolean)
}

function closeDialog() {
  if (props.submitting) return
  resetForm()
  emit('close')
}

function submitForm() {
  if (!validateForm()) return

  emit('submit', {
    name: name.value,
    collectionName: collectionName.value,
    modelCode: modelCode.value,
    description: description.value,
    categoryId: categoryId.value,
    brandId: brandId.value,
    isActive: isActive.value,
    attributeIds: attributeIds.value,
    attributeDefinitions: attributeDefinitions.value.map(a => ({ productAttributeId: a.id, scope: a.scope, commonCharacteristicValueId: a.scope === 1 ? a.commonCharacteristicValueId : null }))
  })
}

watch(
  () => props.visible,
  async (val) => {
    if (val) {
      clearLocalErrors()
      await loadData()
    }
  }
)

watch(
  () => props.productType,
  (val) => {
    if (val) {
      name.value = val.name ?? ''
      collectionName.value = val.collectionName ?? ''
      modelCode.value = val.modelCode ?? ''
      description.value = val.description ?? ''
      categoryId.value = val.categoryId ?? null
      brandId.value = val.brandId ?? null
      isActive.value = val.isActive ?? true

      const existing = val.productTypeAttributes ?? val.attributes ?? []
      attributeIds.value = existing.map(a => a.productAttribute?.id ?? a.productAttributeId ?? a.id)
      attributeDefinitions.value = existing.map(a => { const attr = a.productAttribute ?? a; return { id: attr.id ?? attr.Id, name: attr.name ?? attr.Name, values: attr.values ?? attr.Values ?? [], scope: a.scope ?? a.Scope ?? 0, commonCharacteristicValueId: a.selectedValueId ?? a.SelectedValueId ?? a.commonCharacteristicValueId ?? a.CommonCharacteristicValueId ?? null } })
    } else {
      resetForm()
    }
  },
  { immediate: true }
)

onMounted(loadData)
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(520px, 95vw)' }"
    @update:visible="closeDialog"
  >
    <div class="form">
      <div class="field">
        <label for="product-type-name">Название</label>
        <InputText
          id="product-type-name"
          v-model="name"
          :invalid="hasFieldError('name')"
          @input="localErrors.name = ''"
        />
        <small v-if="hasFieldError('name')" class="field-error">
          {{ getFieldError('name') }}
        </small>
      </div>

      <div class='field'>
        <label for='product-type-collection'>Коллекция</label>
        <InputText
          id='product-type-collection'
          v-model='collectionName'
        />
      </div>

      <div class='field'>
        <label for='product-type-model-code'>Код модели</label>
        <InputText
          id='product-type-model-code'
          v-model='modelCode'
        />
      </div>

      <div class="field">
        <label for="product-type-description">Описание</label>
        <InputText
          id="product-type-description"
          v-model="description"
          aria-describedby="product-type-description-count"
          :invalid="hasFieldError('description')"
          @input="localErrors.description = ''"
        />
        <small id="product-type-description-count">{{ description.length }} / {{ descriptionMaxLength }}</small>
        <small v-if="hasFieldError('description')" class="field-error">
          {{ getFieldError('description') }}
        </small>
      </div>

      <div class="field">
        <label for="product-type-category">Категория</label>
        <Dropdown
          id="product-type-category"
          v-model="categoryId"
          :options="categories"
          optionLabel="name"
          optionValue="id"
          placeholder="Выберите категорию"
          :loading="loadingOptions"
          :invalid="hasFieldError('categoryId')"
          @change="localErrors.categoryId = ''"
        />
        <small v-if="hasFieldError('categoryId')" class="field-error">
          {{ getFieldError('categoryId') }}
        </small>
      </div>

      <div class="field">
        <label for="product-type-brand">Бренд</label>
        <Dropdown
          id="product-type-brand"
          v-model="brandId"
          :options="brands"
          optionLabel="name"
          optionValue="id"
          placeholder="Выберите бренд"
          :loading="loadingOptions"
          :invalid="hasFieldError('brandId')"
          @change="localErrors.brandId = ''"
        />
        <small v-if="hasFieldError('brandId')" class="field-error">
          {{ getFieldError('brandId') }}
        </small>
      </div>

      <div class="field">
        <label for="product-type-attributes">Атрибуты</label>
        <MultiSelect
          id="product-type-attributes"
          v-model="attributeIds"
          :options="attributes"
          optionLabel="name"
          optionValue="id"
          placeholder="Выберите атрибуты"
          :loading="loadingOptions"
          :invalid="hasFieldError('attributeIds')"
          @change="attributeDefinitions = attributeIds.map(id => { const old = attributeDefinitions.find(a => a.id === id); const attr = attributes.find(a => a.id === id); return old ?? { id, name: attr?.name, values: attr?.values ?? [], scope: 0, commonCharacteristicValueId: null } }); localErrors.attributeIds = ''"
        />
        <div v-for="definition in attributeDefinitions" :key="definition.id" class="attribute-definition">
          <span>{{ definition.name }}</span>
          <Dropdown v-model="definition.scope" :options="scopes" optionLabel="label" optionValue="value" @change="definition.commonCharacteristicValueId = definition.scope === 1 ? definition.commonCharacteristicValueId : null" />
          <Dropdown v-if="definition.scope === 1" v-model="definition.commonCharacteristicValueId" :options="definition.values" optionLabel="value" optionValue="id" placeholder="Выберите значение" />
          <small v-if="definition.scope === 1 && !definition.commonCharacteristicValueId" class="field-error">Выберите значение</small>
        </div>
        <small v-if="hasFieldError('attributeIds')" class="field-error">
          {{ getFieldError('attributeIds') }}
        </small>
      </div>

      <div class="field checkbox">
        <Checkbox
          v-model="isActive"
          inputId="product-type-active"
          binary
        />
        <label for="product-type-active">Активный</label>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="props.submitting"
        @click="closeDialog"
      />
      <Button
        :label="submitLabel"
        icon="pi pi-check"
        :loading="props.submitting"
        :disabled="props.submitting"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.checkbox {
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.field-error {
  font-size: 12px;
  color: #dc2626;
}

:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-multiselect) {
  width: 100%;
  border-radius: 12px;
}

:deep(.p-inputtext.p-invalid),
:deep(.p-dropdown.p-invalid),
:deep(.p-multiselect.p-invalid) {
  border-color: #dc2626;
}
</style>
